import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { refresh } from '../actions/authActions';
import { getAdminUser } from '../actions/userActions';
import { createLoadingSelector } from '../apis/selectors';
import PosthogService from '../services/posthog/PosthogService';
import { getPosthogConfig } from '../services/posthog/config';

const loadingSelector = createLoadingSelector(['GET_ADMIN_USER']);

export const useInitialLoading = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const accessToken = useSelector((state) => state.auth.access_token);
  const adminUser = useSelector((state) => state.users.adminUser);
  const posthogInitialized = useRef(false);

  const isFetchingCurrentUser = useSelector((state) => loadingSelector(state));

  // Initialize PostHog once on mount
  useEffect(() => {
    if (!posthogInitialized.current) {
      const posthog = PosthogService.getInstance();
      const config = getPosthogConfig();
      posthog.initialize(config);
      posthogInitialized.current = true;
    }
  }, []);

  // Identify user with PostHog when adminUser is available
  useEffect(() => {
    if (adminUser?.id) {
      const posthog = PosthogService.getInstance();
      posthog.identify(adminUser.id, {
        email: adminUser.email_id,
        name: adminUser.full_name,
        environment: process.env.REACT_APP_TEMP_MEE_ENV || 'development',
      });
    }
  }, [adminUser?.id, adminUser?.email_id, adminUser?.full_name]);

  // Initial token refresh on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken) {
        await dispatch(refresh());
        await dispatch(getAdminUser());
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, [dispatch]);

  // Fetch admin user when access token becomes available (refresh, sign in, or SSO)
  useEffect(() => {
    if (accessToken && !adminUser?.id) {
      dispatch(getAdminUser());
    }
  }, [accessToken, adminUser?.id, dispatch]);

  return {
    isLoading: isLoading || isFetchingCurrentUser,
    isSignedIn: !!accessToken,
  };
};
